import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Form,
  TextField,
  Select,
  Button, ChoiceList, Checkbox, Banner, List
} from '@shopify/polaris';
import { SaveBar, useAppBridge } from '@shopify/app-bridge-react';
import useAxiosClient from '../axiosClient';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AddStaff() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const appBridge = useAppBridge();
  // Add this with your other state declarations
  const [locations, setLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  // Default availability: Mon–Sat available 09:00–17:00, Sunday off
  const DEFAULT_AVAILABILITY = {
    monday: { available: true },
    tuesday: { available: true },
    wednesday: { available: true },
    thursday: { available: true },
    friday: { available: true },
    saturday: { available: true },
    sunday: { available: false },
  };

  const DAY_LABELS = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  const [staffInfo, setStaffInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    permission: 'Owner',
    pinCode: '',
    selectedLocationType: 'All',
    selectedLocations: [],
    currency: 'USD',
    payrollType: 'Hourly',
    salaryAmount: '0',
    taxType: '',
    taxAmount: '0',
    paymentMethod: '',
    paymentEmail: '',
    staffType: 'shopify staff',
    paypalName: '',
    stripeEmail: '',
    stripeAccountId: '',
    wiseEmail: '',
    wiseAccountName: '',
    wiseCurrency: 'USD',
    payoneerEmail: '',
    payoneerName: '',
    bankAccountType: 'domestic',
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    routingNumber: '',
    swiftCode: '',
    iban: '',
    revolutContact: '',
    revolutUsername: '',
    venmoUsername: '',
    venmoPhone: '',
    cashTag: '',
    cashAppContact: '',
    availability: DEFAULT_AVAILABILITY,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [savebar, setSaveBar] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [errors, setErrors] = useState({});
  const originalData = useRef(staffInfo);
  const [showErrorBanner, setShowErrorBanner] = useState(true);
  const CURRENCIES = [
    { label: 'US Dollar (USD)', value: 'USD' },
    { label: 'Euro (EUR)', value: 'EUR' },
    { label: 'British Pound (GBP)', value: 'GBP' },
    { label: 'Japanese Yen (JPY)', value: 'JPY' },
    { label: 'Australian Dollar (AUD)', value: 'AUD' },
    { label: 'Canadian Dollar (CAD)', value: 'CAD' },
    { label: 'Swiss Franc (CHF)', value: 'CHF' },
    { label: 'Chinese Yuan (CNY)', value: 'CNY' },
    { label: 'Indian Rupee (INR)', value: 'INR' },
    { label: 'Singapore Dollar (SGD)', value: 'SGD' },
    { label: 'New Zealand Dollar (NZD)', value: 'NZD' },
    { label: 'Hong Kong Dollar (HKD)', value: 'HKD' },
    { label: 'Swedish Krona (SEK)', value: 'SEK' },
    { label: 'South Korean Won (KRW)', value: 'KRW' },
    { label: 'Brazilian Real (BRL)', value: 'BRL' },
    { label: 'Russian Ruble (RUB)', value: 'RUB' },
    { label: 'South African Rand (ZAR)', value: 'ZAR' },
    { label: 'Mexican Peso (MXN)', value: 'MXN' },
    { label: 'United Arab Emirates Dirham (AED)', value: 'AED' },
    { label: 'Danish Krone (DKK)', value: 'DKK' },
    { label: 'Norwegian Krone (NOK)', value: 'NOK' },
    { label: 'Polish Złoty (PLN)', value: 'PLN' },
    { label: 'Turkish Lira (TRY)', value: 'TRY' },
    { label: 'Thai Baht (THB)', value: 'THB' },
    { label: 'Indonesian Rupiah (IDR)', value: 'IDR' },
    { label: 'Czech Koruna (CZK)', value: 'CZK' },
    { label: 'Hungarian Forint (HUF)', value: 'HUF' },
    { label: 'Chilean Peso (CLP)', value: 'CLP' },
    { label: 'Philippine Peso (PHP)', value: 'PHP' },
    { label: 'Malaysian Ringgit (MYR)', value: 'MYR' },
    { label: 'Colombian Peso (COP)', value: 'COP' },
    { label: 'Argentine Peso (ARS)', value: 'ARS' },
    { label: 'Romanian Leu (RON)', value: 'RON' },
    { label: 'Croatian Kuna (HRK)', value: 'HRK' }, // Now replaced by EUR, but included for legacy
    { label: 'Bulgarian Lev (BGN)', value: 'BGN' },
    { label: 'Egyptian Pound (EGP)', value: 'EGP' },
    { label: 'Nigerian Naira (NGN)', value: 'NGN' },
    { label: 'Qatari Riyal (QAR)', value: 'QAR' },
    { label: 'Kuwaiti Dinar (KWD)', value: 'KWD' },
    { label: 'Saudi Riyal (SAR)', value: 'SAR' },
    { label: 'Israeli New Shekel (ILS)', value: 'ILS' },
    { label: 'Ukrainian Hryvnia (UAH)', value: 'UAH' },
    { label: 'Pakistani Rupee (PKR)', value: 'PKR' },
    { label: 'Bangladeshi Taka (BDT)', value: 'BDT' },
    { label: 'Vietnamese Dong (VND)', value: 'VND' },
    { label: 'Icelandic Króna (ISK)', value: 'ISK' },
    { label: 'Peruvian Sol (PEN)', value: 'PEN' },
    { label: 'Moroccan Dirham (MAD)', value: 'MAD' },
    { label: 'Kenyan Shilling (KES)', value: 'KES' },
    { label: 'Uruguayan Peso (UYU)', value: 'UYU' },
    { label: 'Costa Rican Colón (CRC)', value: 'CRC' },
    { label: 'Kazakhstani Tenge (KZT)', value: 'KZT' },
    { label: 'Bahraini Dinar (BHD)', value: 'BHD' },
    { label: 'Omani Rial (OMR)', value: 'OMR' },
    { label: 'Jordanian Dinar (JOD)', value: 'JOD' },
    { label: 'Tunisian Dinar (TND)', value: 'TND' },
    { label: 'Ghanaian Cedi (GHS)', value: 'GHS' },
    { label: 'Bolivian Boliviano (BOB)', value: 'BOB' },
    { label: 'Sri Lankan Rupee (LKR)', value: 'LKR' },
    { label: 'Nepalese Rupee (NPR)', value: 'NPR' },
    { label: 'Macanese Pataca (MOP)', value: 'MOP' },
    { label: 'Cambodian Riel (KHR)', value: 'KHR' },
    { label: 'Taiwanese Dollar (TWD)', value: 'TWD' },
    { label: 'Myanmar Kyat (MMK)', value: 'MMK' },
    { label: 'Mongolian Tugrik (MNT)', value: 'MNT' },
    { label: 'Paraguayan Guaraní (PYG)', value: 'PYG' },
    { label: 'Honduran Lempira (HNL)', value: 'HNL' },
    { label: 'Jamaican Dollar (JMD)', value: 'JMD' },
    { label: 'Fijian Dollar (FJD)', value: 'FJD' },
    { label: 'Mauritian Rupee (MUR)', value: 'MUR' },
    { label: 'Seychellois Rupee (SCR)', value: 'SCR' },
    { label: 'Maldivian Rufiyaa (MVR)', value: 'MVR' },
    { label: 'Namibian Dollar (NAD)', value: 'NAD' },
    { label: 'Botswanan Pula (BWP)', value: 'BWP' },
    { label: 'Tanzanian Shilling (TZS)', value: 'TZS' },
    { label: 'Ugandan Shilling (UGX)', value: 'UGX' },
    { label: 'Rwandan Franc (RWF)', value: 'RWF' },
    { label: 'Ethiopian Birr (ETB)', value: 'ETB' },
    { label: 'Libyan Dinar (LYD)', value: 'LYD' },
    { label: 'Angolan Kwanza (AOA)', value: 'AOA' },
    { label: 'Mozambican Metical (MZN)', value: 'MZN' },
    { label: 'Zambian Kwacha (ZMW)', value: 'ZMW' },
    { label: 'Afghan Afghani (AFN)', value: 'AFN' },
    { label: 'Albanian Lek (ALL)', value: 'ALL' },
    { label: 'Armenian Dram (AMD)', value: 'AMD' },
    { label: 'Netherlands Antillean Guilder (ANG)', value: 'ANG' },
    { label: 'Aruban Florin (AWG)', value: 'AWG' },
    { label: 'Azerbaijani Manat (AZN)', value: 'AZN' },
    { label: 'Bosnia-Herzegovina Convertible Mark (BAM)', value: 'BAM' },
    { label: 'Barbadian Dollar (BBD)', value: 'BBD' },
    { label: 'Belarusian Ruble (BYN)', value: 'BYN' },
    { label: 'Belize Dollar (BZD)', value: 'BZD' },
    { label: 'Bermudian Dollar (BMD)', value: 'BMD' },
    { label: 'Bhutanese Ngultrum (BTN)', value: 'BTN' },
    { label: 'Brunei Dollar (BND)', value: 'BND' },
    { label: 'Burundian Franc (BIF)', value: 'BIF' },
    { label: 'Cape Verdean Escudo (CVE)', value: 'CVE' },
    { label: 'Cayman Islands Dollar (KYD)', value: 'KYD' },
    { label: 'Central African CFA Franc (XAF)', value: 'XAF' },
    { label: 'CFP Franc (XPF)', value: 'XPF' },
    { label: 'Comorian Franc (KMF)', value: 'KMF' },
    { label: 'Congolese Franc (CDF)', value: 'CDF' },
    { label: 'Cuban Peso (CUP)', value: 'CUP' },
    { label: 'Djiboutian Franc (DJF)', value: 'DJF' },
    { label: 'Dominican Peso (DOP)', value: 'DOP' },
    { label: 'Eritrean Nakfa (ERN)', value: 'ERN' },
    { label: 'Falkland Islands Pound (FKP)', value: 'FKP' },
    { label: 'Gambian Dalasi (GMD)', value: 'GMD' },
    { label: 'Georgian Lari (GEL)', value: 'GEL' },
    { label: 'Guatemalan Quetzal (GTQ)', value: 'GTQ' },
    { label: 'Guinean Franc (GNF)', value: 'GNF' },
    { label: 'Guyanese Dollar (GYD)', value: 'GYD' },
    { label: 'Haitian Gourde (HTG)', value: 'HTG' },
    { label: 'Iranian Rial (IRR)', value: 'IRR' },
    { label: 'Iraqi Dinar (IQD)', value: 'IQD' },
    { label: 'Laotian Kip (LAK)', value: 'LAK' },
    { label: 'Lebanese Pound (LBP)', value: 'LBP' },
    { label: 'Lesotho Loti (LSL)', value: 'LSL' },
    { label: 'Liberian Dollar (LRD)', value: 'LRD' },
    { label: 'Malagasy Ariary (MGA)', value: 'MGA' },
    { label: 'Malawian Kwacha (MWK)', value: 'MWK' },
    { label: 'Moldovan Leu (MDL)', value: 'MDL' },
    { label: 'Burmese Kyat (MMK)', value: 'MMK' },
    { label: 'Nicaraguan Córdoba (NIO)', value: 'NIO' },
    { label: 'North Korean Won (KPW)', value: 'KPW' },
    { label: 'Panamanian Balboa (PAB)', value: 'PAB' },
    { label: 'Papua New Guinean Kina (PGK)', value: 'PGK' },
    { label: 'Saint Helena Pound (SHP)', value: 'SHP' },
    { label: 'Samoan Tala (WST)', value: 'WST' },
    { label: 'São Tomé & Príncipe Dobra (STN)', value: 'STN' },
    { label: 'Serbian Dinar (RSD)', value: 'RSD' },
    { label: 'Sierra Leonean Leone (SLE)', value: 'SLE' },
    { label: 'Solomon Islands Dollar (SBD)', value: 'SBD' },
    { label: 'Somali Shilling (SOS)', value: 'SOS' },
    { label: 'South Sudanese Pound (SSP)', value: 'SSP' },
    { label: 'Sudanese Pound (SDG)', value: 'SDG' },
    { label: 'Surinamese Dollar (SRD)', value: 'SRD' },
    { label: 'Syrian Pound (SYP)', value: 'SYP' },
    { label: 'Tajikistani Somoni (TJS)', value: 'TJS' },
    { label: 'Tongan Paʻanga (TOP)', value: 'TOP' },
    { label: 'Transnistrian Ruble (PRB)', value: 'PRB' },
    { label: 'Turkmenistani Manat (TMT)', value: 'TMT' },
    { label: 'Tuvaluan Dollar (TVD)', value: 'TVD' },
    { label: 'Vanuatu Vatu (VUV)', value: 'VUV' },
    { label: 'Venezuelan Bolívar (VES)', value: 'VES' },
    { label: 'Yemeni Rial (YER)', value: 'YER' },
    { label: 'Zimbabwean Dollar (ZWL)', value: 'ZWL' },
    { label: 'East Caribbean Dollar (XCD)', value: 'XCD' },
    { label: 'West African CFA Franc (XOF)', value: 'XOF' },
  ];

  const handleAvailabilityChange = (day, field, value) => {
    setStaffInfo(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: { ...prev.availability[day], [field]: value },
      },
    }));
    setIsDirty(true);
  };

  // Track input changes and mark form as dirty
  const handleChange = (field) => (value) => {
    setStaffInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);

    // Clear the error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  const validateForm = () => {
    const newErrors = {};

    // Basic Contact Information Validation
    if (!staffInfo.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (staffInfo.firstName.length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    if (!staffInfo.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (staffInfo.lastName.length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    if (!staffInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(staffInfo.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (staffInfo.phone && !/^\+?[\d\s-]{10,}$/.test(staffInfo.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // PIN Code Validation
    if (!staffInfo.pinCode) {
      newErrors.pinCode = 'PIN code is required';
    } else if (!/^\d{4}$/.test(staffInfo.pinCode)) {
      newErrors.pinCode = 'PIN must be 4 digits';
    }

    // Location Validation
    if (staffInfo.selectedLocationType === 'Specific' &&
      (!staffInfo.selectedLocations || staffInfo.selectedLocations.length === 0)) {
      newErrors.locations = 'Please select at least one location';
    }

    // Tax Validation - only when a tax type has actually been chosen.
    if (staffInfo.taxType) {
      const taxValue = parseFloat(staffInfo.taxAmount);
      if (staffInfo.taxAmount === '' || isNaN(taxValue) || taxValue < 0) {
        newErrors.taxAmount = t('tax_amount_invalid');
      } else if (staffInfo.taxType === 'percentage' && taxValue > 100) {
        newErrors.taxAmount = t('tax_percentage_max');
      }
    }

    // Salary/Wages Validation
    if (!staffInfo.salaryAmount || staffInfo.salaryAmount === '0') {
      newErrors.salaryAmount = 'Please enter a valid amount';
    } else if (isNaN(parseFloat(staffInfo.salaryAmount))) {
      newErrors.salaryAmount = 'Please enter a valid number';
    }

    // Payment Method Validation
    if (staffInfo.paymentMethod) {
      switch (staffInfo.paymentMethod) {
        case 'PayPal':
          if (!staffInfo.paymentEmail) {
            newErrors.paymentEmail = 'PayPal email is required';
          }
          if (!staffInfo.paypalName) {
            newErrors.paypalName = 'PayPal account name is required';
          }
          break;

        case 'Stripe':
          if (!staffInfo.stripeEmail) {
            newErrors.stripeEmail = 'Stripe email is required';
          }
          if (!staffInfo.stripeAccountId) {
            newErrors.stripeAccountId = 'Stripe account ID is required';
          }
          break;

        case 'Wise':
          if (!staffInfo.wiseEmail) {
            newErrors.wiseEmail = 'Wise email is required';
          }
          if (!staffInfo.wiseAccountName) {
            newErrors.wiseAccountName = 'Account holder name is required';
          }
          break;

        case 'Payoneer':
          if (!staffInfo.payoneerEmail) {
            newErrors.payoneerEmail = 'Payoneer email is required';
          }
          if (!staffInfo.payoneerName) {
            newErrors.payoneerName = 'Account name is required';
          }
          break;

        case 'Direct Deposit':
        case 'Bank Transfer':
          if (!staffInfo.bankName) {
            newErrors.bankName = 'Bank name is required';
          }
          if (!staffInfo.accountHolderName) {
            newErrors.accountHolderName = 'Account holder name is required';
          }
          if (!staffInfo.accountNumber) {
            newErrors.accountNumber = 'Account number is required';
          }
          if (staffInfo.bankAccountType === 'domestic') {
            if (!staffInfo.routingNumber) {
              newErrors.routingNumber = 'Routing number is required';
            }
          } else {
            if (!staffInfo.swiftCode) {
              newErrors.swiftCode = 'SWIFT/BIC code is required';
            }
            if (!staffInfo.iban) {
              newErrors.iban = 'IBAN is required';
            }
          }
          break;

        case 'Revolut':
          if (!staffInfo.revolutContact) {
            newErrors.revolutContact = 'Revolut email/phone is required';
          }
          if (!staffInfo.revolutUsername) {
            newErrors.revolutUsername = 'Revolut username is required';
          }
          break;

        case 'Venmo':
          if (!staffInfo.venmoUsername) {
            newErrors.venmoUsername = 'Venmo username is required';
          }
          if (!staffInfo.venmoPhone) {
            newErrors.venmoPhone = 'Venmo phone number is required';
          }
          break;

        case 'Square':
          if (!staffInfo.cashTag) {
            newErrors.cashTag = 'Cash App $Cashtag is required';
          }
          if (!staffInfo.cashAppContact) {
            newErrors.cashAppContact = 'Cash App contact info is required';
          }
          break;
      }
    }

    // Set the errors and return validation result
    setErrors(newErrors);

    // If there are any errors, show a toast message
    if (Object.keys(newErrors).length > 0) {
      setShowErrorBanner(true);
      shopify.toast.show('Fields Required', { error: true });
    }

    return Object.keys(newErrors).length === 0;
  };

  const valueRef = useRef(staffInfo); // Create a ref to store the value
  useEffect(() => {
    valueRef.current = staffInfo;
  }, [staffInfo]);

  const handleSave = async () => {
    if (!validateForm()) {
      // shopify.toast.show('Please fix the validation errors before saving', { error: true });
      appBridge.saveBar.hide('create-shopify-save-bar');
      return;
    }

    try {
      const allLocationNames = locations.map(location => location.name).join(', ');
      const dataToSend = {
        ...valueRef.current,
        locations: valueRef.current.selectedLocationType === 'All'
          ? allLocationNames
          : valueRef.current.selectedLocations.join(', ')
      };

      const response = await axiosClient.post('/staff', dataToSend);
      if (response.data.success) {
        originalData.current = staffInfo;
        setIsDirty(false);
        shopify.toast.show('Staff Created')
        appBridge.saveBar.hide('create-shopify-save-bar');
        navigate('/StaffManagement')
      } else {
        // Handle server validation errors
        if (response.data.errors) {
          setErrors(response.data.errors);
          // Show first error message in toast
          const firstError = Object.values(response.data.errors)[0];
          shopify.toast.show(firstError[0] || 'Validation failed', { error: true });
        } else {
          shopify.toast.show(response.data.message || 'Failed to add staff', { error: true });
        }
        appBridge.saveBar.hide('create-shopify-save-bar');
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        // Handle validation errors from server
        setErrors(error.response.data.errors);
        shopify.toast.show(error.response.data.message || Object.values(error.response.data.errors)[0], { error: true });
      } else {
        shopify.toast.show('Error adding staff ', { error: true });
      }
      appBridge.saveBar.hide('create-shopify-save-bar');
    }
  };

  // Discard changes and reset form
  const handleDiscard = () => {
    setStaffInfo(originalData.current);
    setIsDirty(false);
    setErrors({});
    appBridge.saveBar.hide('create-shopify-save-bar');
  };


  // Add this after your state declarations
  const fetchLocations = async () => {
    try {
      setIsLoadingLocations(true);
      const response = await axiosClient.get('/locations');
      if (response.data.success) {
        setLocations(response.data.locations);
      } else {
        shopify.toast.show('Failed to load locations', { error: true });
      }
    } catch (error) {
      shopify.toast.show('Error loading locations', { error: true });
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // Add this useEffect to fetch locations when component mounts
  useEffect(() => {
    fetchLocations();
  }, []);

  // Add this useEffect to ensure isDirty is set correctly when form values change
  useEffect(() => {
    // If any field has changed from its original value, mark form as dirty
    const hasChanges = JSON.stringify(staffInfo) !== JSON.stringify(originalData.current);
    setIsDirty(hasChanges);
  }, [staffInfo]);


  const handleBackClick = useCallback(() => {
    if (isDirty) {
      appBridge.saveBar.leaveConfirmation()
        .then(() => {
          // User confirmed leaving or no changes to save
          navigate("/StaffManagement");
        })
        .catch(() => {
          // User cancelled leaving
        });
    } else {
      // No unsaved changes, navigate directly
      navigate("/StaffManagement");
    }
  }, [isDirty, appBridge, navigate]);

  return (
    <Page
      title={t('add_shopify_staff')}
      fullWidth
      backAction={{
        content: t('staff_management'),
        onAction: handleBackClick
      }}
    >
      {Object.keys(errors).length > 0 && showErrorBanner && (
        <>
          <Banner
            title="Please correct the following errors:"
            tone="critical"
            onDismiss={() => setShowErrorBanner(false)}
          >
            <List type="bullet">
              {Object.entries(errors).map(([field, messages]) => (
                <List.Item key={field}>
                  {`${field.charAt(0).toUpperCase() + field.slice(1)}: ${Array.isArray(messages) ? messages[0] : messages}`}
                </List.Item>
              ))}
            </List>
          </Banner>
          <div className='mb-2'></div>
        </>
      )}


      <Form onSubmit={handleSave}>
        <Layout>
          {/* Contact Information */}
          <Layout.AnnotatedSection
            title={t('basic_information')}
            description={t('manage_contact_information_and_permissions')}
          >
            <Card sectioned>
              <div className="flex gap-6">
                <div className="w-full md:w-1/2">
                  <TextField
                    label={t('first_name')}
                    value={staffInfo.firstName}
                    onChange={handleChange('firstName')}
                    placeholder={t('first_name')}
                    error={errors.firstName && Array.isArray(errors.firstName) ? errors.firstName[0] : errors.firstName}
                  />
                </div>
                <div className="w-full md:w-1/2">
                  <TextField
                    label={t('last_name')}
                    value={staffInfo.lastName}
                    onChange={handleChange('lastName')}
                    placeholder={t('last_name')}
                    error={errors.lastName && Array.isArray(errors.lastName) ? errors.email[0] : errors.lastName}
                  />
                </div>
              </div>
              <div className="flex gap-6 mt-4">
                <div className="w-full md:w-1/2">
                  <TextField
                    label={t('email')}
                    type="email"
                    value={staffInfo.email}
                    onChange={handleChange('email')}
                    placeholder={t('email')}
                    error={errors.email && Array.isArray(errors.email) ? errors.email[0] : errors.email}
                  />
                </div>
                <div className="w-full md:w-1/2">
                  <TextField
                    label={t('phone_optional')}
                    type="tel"
                    value={staffInfo.phone}
                    onChange={handleChange('phone')}
                    placeholder={t('phone')}
                  />
                </div>
              </div>
            </Card>
          </Layout.AnnotatedSection>

          {/* Position */}
          <Layout.AnnotatedSection
            title={t('position')}
            description={t('permissions')}
          >
            <Card sectioned>
              <Select
                label={t('position')}
                options={[
                  { label: t('owner'), value: 'Owner' },
                  { label: t('staff'), value: 'Staff' },
                  { label: t('manager'), value: 'Manager' },
                ]}
                value={staffInfo.permission}
                onChange={handleChange('permission')}
                placeholder={t('position')}
                error={errors.permission}
              />
            </Card>
          </Layout.AnnotatedSection>

          {/* POS PIN Code */}
          <Layout.AnnotatedSection
            title={t('pin_code')}
            description={t('pin_code')}
          >
            <Card sectioned>
              <div className="space-y-2">
                <p className="text-gray-700">
                  Staff need a unique 4 digit PIN to access the Zon POS app
                </p>
                <div className="flex gap-4">
                  <div className="w-28">
                    <TextField
                      label={t('pin_code')}
                      type="number"
                      value={staffInfo.pinCode}
                      onChange={handleChange('pinCode')}
                      placeholder=""
                      error={errors.pinCode && Array.isArray(errors.pinCode) ? errors.pinCode[0] : errors.pinCode}
                    />
                  </div>
                  <div style={{ marginTop: "25px", marginLeft: "10px" }}>
                    <Button
                      onClick={() =>
                        setStaffInfo((prev) => ({
                          ...prev,
                          pinCode: Math.floor(1000 + Math.random() * 9000).toString(),
                        }))
                      }
                      size="medium"
                    >
                      Generate Random
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </Layout.AnnotatedSection>

          {/* Locations */}
          <Layout.AnnotatedSection
            title={t('location')}
            description={t('location_access')}
          >
            <Card sectioned>
              <div className="flex items-center gap-6">
                <ChoiceList
                  title=""
                  choices={[
                    { label: t('all_locations'), value: 'All' },
                    { label: t('specific_locations'), value: 'Specific' },
                  ]}
                  selected={[staffInfo.selectedLocationType]}
                  onChange={(value) =>
                    setStaffInfo((prev) => ({
                      ...prev,
                      selectedLocationType: value[0],
                      // Reset selected locations when switching types
                      selectedLocations: value[0] === 'All' ? [] : prev.selectedLocations
                    }))
                  }
                />
              </div>

              {staffInfo.selectedLocationType === 'Specific' && (
                <div className="ml-6 mt-4">
                  <p className="font-semibold">{t('select_locations')}</p>
                  {isLoadingLocations ? (
                    <div>Loading locations...</div>
                  ) : locations.length === 0 ? (
                    <div>No locations available</div>
                  ) : (
                    <div className="space-y-4">
                      {locations.map((location) => (
                        <Checkbox
                          key={location.id}
                          label={
                            <>
                              <strong>{location.name}</strong>
                              <br />
                              <span className="text-sm text-gray-500">
                                {location.address}
                              </span>
                            </>
                          }
                          checked={staffInfo.selectedLocations?.includes(location.name)} // Changed from location.id to location.name
                          onChange={(checked) => {
                            setStaffInfo((prev) => ({
                              ...prev,
                              selectedLocations: checked
                                ? [...(prev.selectedLocations || []), location.name] // Changed from location.id to location.name
                                : (prev.selectedLocations || []).filter(name => name !== location.name) // Changed from id to name
                            }));
                            setIsDirty(true);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          </Layout.AnnotatedSection>


          {/* Salary/Wages */}
          <Layout.AnnotatedSection
            title={t('payroll_information')}
            description={t('configure_payment_methods_and_salary_details')}
          >
            <Card sectioned>
              <div className="flex gap-6">
                <div className="w-full md:w-1/3">
                  <Select
                    label={t('currency')}
                    options={CURRENCIES}
                    value={staffInfo.currency}
                    onChange={handleChange('currency')}
                    helpText={t('currency')}
                    error={errors.currency}
                  />
                </div>
                <div className="w-full md:w-1/3">
                  <Select
                    label={t('payroll_type')}
                    options={[
                      { label: t('hourly'), value: 'Hourly' },
                      { label: t('weekly'), value: 'Weekly' },
                      { label: t('monthly'), value: 'Monthly' },
                    ]}
                    value={staffInfo.payrollType}
                    onChange={handleChange('payrollType')}
                    error={errors.payrollType}
                  />
                </div>
                <div className="w-full md:w-1/3">
                  <TextField
                    label={t('salary_amount')}
                    type="text"
                    value={staffInfo.salaryAmount}
                    onChange={handleChange('salaryAmount')}
                    placeholder=""
                    error={errors.salaryAmount}
                  />
                </div>
                <div className="w-full md:w-1/3">
                  <Select
                    label={t('tax_type')}
                    options={[
                      { label: t('tax_none'), value: '' },
                      { label: t('tax_percentage'), value: 'percentage' },
                      { label: t('tax_fixed'), value: 'fixed' },
                    ]}
                    value={staffInfo.taxType}
                    onChange={handleChange('taxType')}
                    error={errors.taxType}
                  />
                </div>
                {staffInfo.taxType ? (
                  <div className="w-full md:w-1/3">
                    <TextField
                      label={staffInfo.taxType === 'percentage' ? t('tax_rate') : t('tax_amount')}
                      type="text"
                      value={staffInfo.taxAmount}
                      onChange={handleChange('taxAmount')}
                      placeholder={staffInfo.taxType === 'percentage' ? '15' : '50'}
                      suffix={staffInfo.taxType === 'percentage' ? '%' : undefined}
                      helpText={t('tax_help')}
                      error={errors.taxAmount}
                    />
                  </div>
                ) : null}
              </div>
            </Card>
          </Layout.AnnotatedSection>

          {/* Availability */}
          <Layout.AnnotatedSection
            title="Weekly Availability"
            description="Set the days this staff member is available to work. Managers will see a warning if they schedule outside these days."
          >
            <Card sectioned>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {DAY_LABELS.map(({ key, label }) => {
                  const isAvailable = staffInfo.availability[key].available;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleAvailabilityChange(key, 'available', !isAvailable)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        border: `2px solid ${isAvailable ? '#008060' : '#C9CCCF'}`,
                        backgroundColor: isAvailable ? '#008060' : '#F6F6F7',
                        color: isAvailable ? '#FFFFFF' : '#6D7175',
                        fontWeight: isAvailable ? '600' : '400',
                        fontSize: '13px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        userSelect: 'none',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <p style={{ marginTop: '10px', fontSize: '12px', color: '#6D7175' }}>
                Click a day to toggle availability. Green = available.
              </p>
            </Card>
          </Layout.AnnotatedSection>

          {/* Payment Method */}
          <Layout.AnnotatedSection
            title={t('payment_method')}
            description="Choose the preferred payment method for international or local payments."
          >
            <Card sectioned>
              <div className="space-y-4">
                <Select
                  label={t('payment_method')}
                  options={[
                    { label: t('paypal'), value: 'PayPal' },
                    { label: t('stripe'), value: 'Stripe' },
                    { label: t('wise'), value: 'Wise' },
                    { label: t('payoneer'), value: 'Payoneer' },
                    { label: t('bank_transfer'), value: 'Bank Transfer' },
                    { label: 'Direct Deposit', value: 'Direct Deposit' },
                    { label: 'Cash', value: 'Cash' },
                    { label: 'Check', value: 'Check' },
                    { label: t('revolut'), value: 'Revolut' },
                    { label: t('venmo'), value: 'Venmo' },
                    { label: t('square'), value: 'Square' }
                  ]}
                  value={staffInfo.paymentMethod}
                  onChange={handleChange('paymentMethod')}
                  placeholder={t('select_payment_method')}
                  error={errors.paymentMethod}
                />

                {/* PayPal Fields */}
                {staffInfo.paymentMethod === 'PayPal' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('paypal_email')}
                      type="email"
                      value={staffInfo.paymentEmail}
                      onChange={handleChange('paymentEmail')}
                      placeholder={t('enter_paypal_email_address')}
                      helpText={t('enter_paypal_email_address')}
                      error={errors.paymentEmail}
                    />
                    <TextField
                      label={t('paypal_account_name')}
                      value={staffInfo.paypalName || ''}
                      onChange={handleChange('paypalName')}
                      placeholder={t('account_holder_name_in_paypal')}
                      error={errors.paypalName}
                    />
                  </div>
                )}

                {/* Stripe Fields */}
                {staffInfo.paymentMethod === 'Stripe' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('stripe_account_email')}
                      type="email"
                      value={staffInfo.stripeEmail || ''}
                      onChange={handleChange('stripeEmail')}
                      placeholder={t('stripe_account_email_address')}
                      error={errors.stripeEmail}
                    />
                    <TextField
                      label={t('stripe_account_id')}
                      value={staffInfo.stripeAccountId || ''}
                      onChange={handleChange('stripeAccountId')}
                      placeholder="acct_xxxxxxxxxxxxx"
                      helpText={t('stripe_connect_account_id')}
                      error={errors.stripeAccountId}
                    />
                  </div>
                )}

                {/* Wise Fields */}
                {staffInfo.paymentMethod === 'Wise' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('wise_email')}
                      type="email"
                      value={staffInfo.wiseEmail || ''}
                      onChange={handleChange('wiseEmail')}
                      placeholder={t('wise_account_email')}
                      error={errors.wiseEmail}
                    />
                    <TextField
                      label={t('wise_account_holder_name')}
                      value={staffInfo.wiseAccountName || ''}
                      onChange={handleChange('wiseAccountName')}
                      placeholder={t('name_on_wise_account')}
                      error={errors.wiseAccountName}
                    />
                    <Select
                      label={t('wise_account_currency')}
                      options={[
                        { label: 'USD', value: 'USD' },
                        { label: 'EUR', value: 'EUR' },
                        { label: 'GBP', value: 'GBP' },
                        { label: 'AUD', value: 'AUD' },
                        { label: 'CAD', value: 'CAD' }
                      ]}
                      value={staffInfo.wiseCurrency || 'USD'}
                      onChange={handleChange('wiseCurrency')}
                      error={errors.wiseCurrency}
                    />
                  </div>
                )}

                {/* Payoneer Fields */}
                {staffInfo.paymentMethod === 'Payoneer' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('payoneer_email')}
                      type="email"
                      value={staffInfo.payoneerEmail || ''}
                      onChange={handleChange('payoneerEmail')}
                      placeholder={t('payoneer_account_email')}
                      error={errors.payoneerEmail}
                    />
                    <TextField
                      label={t('payoneer_account_name')}
                      value={staffInfo.payoneerName || ''}
                      onChange={handleChange('payoneerName')}
                      placeholder={t('name_on_payoneer_account')}
                      error={errors.payoneerName}
                    />
                  </div>
                )}

                {/* Bank Transfer / Direct Deposit Fields */}
                {(staffInfo.paymentMethod === 'Bank Transfer' || staffInfo.paymentMethod === 'Direct Deposit') && (
                  <div className="space-y-4">
                    <Select
                      label={t('bank_account_type')}
                      options={[
                        { label: t('domestic'), value: 'domestic' },
                        { label: t('international_swift'), value: 'international' }
                      ]}
                      value={staffInfo.bankAccountType || 'domestic'}
                      onChange={handleChange('bankAccountType')}
                      error={errors.bankAccountType}
                    />
                    <TextField
                      label={t('bank_name')}
                      value={staffInfo.bankName || ''}
                      onChange={handleChange('bankName')}
                      placeholder={t('enter_bank_name')}
                      error={errors.bankName}
                    />
                    <TextField
                      label={t('account_holder_name')}
                      value={staffInfo.accountHolderName || ''}
                      onChange={handleChange('accountHolderName')}
                      placeholder={t('enter_account_holder_name')}
                      error={errors.accountHolderName}
                    />
                    <TextField
                      label={t('account_number')}
                      value={staffInfo.accountNumber || ''}
                      onChange={handleChange('accountNumber')}
                      placeholder={t('enter_account_number')}
                      error={errors.accountNumber}
                    />
                    {staffInfo.bankAccountType === 'domestic' ? (
                      <TextField
                        label={t('routing_number')}
                        value={staffInfo.routingNumber || ''}
                        onChange={handleChange('routingNumber')}
                        placeholder={t('enter_routing_number')}
                        error={errors.routingNumber}
                      />
                    ) : (
                      <>
                        <TextField
                          label={t('swift_bic_code')}
                          value={staffInfo.swiftCode || ''}
                          onChange={handleChange('swiftCode')}
                          placeholder={t('enter_swift_bic_code')}
                          error={errors.swiftCode}
                        />
                        <TextField
                          label={t('iban')}
                          value={staffInfo.iban || ''}
                          onChange={handleChange('iban')}
                          placeholder={t('enter_iban')}
                          error={errors.iban}
                        />
                      </>
                    )}
                  </div>
                )}

                {/* Revolut Fields */}
                {staffInfo.paymentMethod === 'Revolut' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('revolut_email_phone')}
                      value={staffInfo.revolutContact || ''}
                      onChange={handleChange('revolutContact')}
                      placeholder={t('email_or_phone_linked_to_revolut')}
                      error={errors.revolutContact}
                    />
                    <TextField
                      label={t('revolut_username')}
                      value={staffInfo.revolutUsername || ''}
                      onChange={handleChange('revolutUsername')}
                      placeholder={t('username_if_applicable')}
                      error={errors.revolutUsername}
                    />
                  </div>
                )}

                {/* Venmo Fields */}
                {staffInfo.paymentMethod === 'Venmo' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('venmo_username')}
                      value={staffInfo.venmoUsername || ''}
                      onChange={handleChange('venmoUsername')}
                      placeholder="@username"
                      error={errors.venmoUsername}
                    />
                    <TextField
                      label={t('venmo_phone_number')}
                      value={staffInfo.venmoPhone || ''}
                      onChange={handleChange('venmoPhone')}
                      placeholder={t('phone_number_linked_to_venmo')}
                      error={errors.venmoPhone}
                    />
                  </div>
                )}

                {/* Square Cash Fields */}
                {staffInfo.paymentMethod === 'Square' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('cash_app_cashtag')}
                      value={staffInfo.cashTag || ''}
                      onChange={handleChange('cashTag')}
                      placeholder="$cashtag"
                      error={errors.cashTag}
                    />
                    <TextField
                      label={t('cash_app_phone_email')}
                      value={staffInfo.cashAppContact || ''}
                      onChange={handleChange('cashAppContact')}
                      placeholder={t('phone_or_email_linked_to_cash_app')}
                      error={errors.cashAppContact}
                    />
                  </div>
                )}
              </div>
            </Card>
          </Layout.AnnotatedSection>
        </Layout>
      </Form>
      <div style={{ marginTop: "20px" }}></div>

      {isDirty && (
        <SaveBar
          id="create-shopify-save-bar"
          open={isDirty}
        >
          <button
            type="button"
            variant="primary"
            onClick={async () => {
              try {
                await handleSave();
                setIsDirty(false);
              } catch (err) {
                // Error handled silently
              }
            }}
          >
            {t('save')}
          </button>
          <button
            type="button"
            onClick={handleDiscard}
          >
            {t('discard')}
          </button>
        </SaveBar>
      )}
    </Page>
  );
}
